import { Clipboard } from '@angular/cdk/clipboard'
import { CommonModule } from '@angular/common'
import type { OnChanges, SimpleChanges, TemplateRef } from '@angular/core'
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, output, signal, ViewChild } from '@angular/core'
import type { Observable } from 'rxjs'
import { EMPTY } from 'rxjs'

import { sleep } from '@app/core/rxjs/sleep'
import { ContextMenuService } from '@app/modules/ui/components/context-menu/context-menu.service'
import { TooltipDirective } from '@app/modules/ui/components/tooltip'
import { Breakpoint2Service } from '@app/modules/ui/services/breakpoint-2.service'
import { UIModule } from '@app/modules/ui/ui.module'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import { MemberService } from '@app/services/member.service'

import type { Conversation } from '../../models/conversation.model'
import type { Message } from '../../models/message.model'
import { isFakeMessage } from '../../models/message.model'
import { FieldBoostComponent } from '../../modules/boost/components/field-boost/field-boost.component'
import { MessagePhotoComponent } from '../../modules/message-photo/components/message-photo/message-photo.component'
import { VoiceMessageComponent } from '../../modules/voice-message/components/voice-message/voice-message.component'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { MessageWrapperComponent } from '../message-wrapper/message-wrapper.component'
import { ConversationStore } from '../store/conversation.store'

@Component({
  selector: 'sb-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  imports: [
    CommonModule,
    UIModule,
    MessagePhotoComponent,
    MessageWrapperComponent,
    TooltipDirective,
    FieldBoostComponent,
    VoiceMessageComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageComponent implements OnChanges {
  readonly conversationStore = inject(ConversationStore)
  readonly memberService = inject(MemberService)
  readonly elementRef = inject(ElementRef)
  private readonly clipboard = inject(Clipboard)
  private readonly popoverService = inject(PopoverService)
  private readonly contextMenuService = inject(ContextMenuService)
  readonly breakpoint2Service = inject(Breakpoint2Service)
  private readonly conversationUIService = inject(ConversationUIService)

  readonly isFakeMessage = isFakeMessage

  @Input({ required: true }) conversation!: Conversation
  @Input({ required: true }) photoUrl!: string | undefined
  @Input({ required: true }) boostable!: boolean
  @Input({ required: true }) message!: Message

  readonly textContentFocused = signal(false)
  @ViewChild(MessagePhotoComponent) readonly messagePhotoComponent: MessagePhotoComponent | undefined
  @ViewChild(VoiceMessageComponent) readonly voiceMessageComponent: VoiceMessageComponent | undefined

  readonly messageChanged = output<void>()

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message) {
      this.messageChanged.emit()
    }
  }

  openContextMenu(
    anchor: HTMLElement,
    contextMenuContentTemplateRef: TemplateRef<any>,
    position: 'bottom_right' | 'bottom_left',
  ): void {
    this.textContentFocused.set(true)
    const { frameViewRef } = this.contextMenuService.createContextMenuContent(
      contextMenuContentTemplateRef,
      anchor,
      position,
    )
    frameViewRef.instance.onClose(() => this.textContentFocused.set(false))
  }

  onTextContentPress(anchor: HTMLElement, contextMenuContentTemplateRef: TemplateRef<any>): void {
    this.closeKeyboard().subscribe({
      complete: () => {
        this.openContextMenu(
          anchor,
          contextMenuContentTemplateRef,
          !this.message.is_me ? 'bottom_right' : 'bottom_left',
        )
      },
    })
  }

  private closeKeyboard(): Observable<boolean> {
    if (
      !(document.activeElement instanceof HTMLInputElement) &&
      !(document.activeElement instanceof HTMLTextAreaElement)
    ) {
      return EMPTY
    }
    document.activeElement.blur()
    return sleep(250)
  }

  copyToClipboard(message: string): void {
    const copied = this.clipboard.copy(message)
    if (copied) {
      this.popoverService.show('nc-check-circle', 'Copied')
    } else {
      // https://material.angular.io/cdk/clipboard/overview#programmatically-copy-a-string
      ErrorHandlers.handleError(
        new Error(`Copying messsage (length ${message.length}) to clipboard failed. Message content: ${message}`),
      )
    }
  }

  onBoostClick(): void {
    if (!this.message.boostInProgress) {
      this.openExistingMessageBoostModal(this.message, this.conversation)
    }
  }

  private openExistingMessageBoostModal(message: Message, conversation: Conversation): void {
    const instance = this.conversationUIService.openExistingMessageBoostModal(conversation)
    instance.onConfirm(() => this.conversationStore.attemptToBoostMessage(message, 'default', conversation))
  }
}
